import React, { useCallback, useMemo } from "react";
import {
  Box,
  Button,
  creatableOptionKey,
  CreatableOptionPicker,
  FieldTrigger,
  Icon,
  Text,
  type CreatableOption,
  type CreatableOptionPickerProps,
  type Omittable,
} from "@salestrak/ui";

import { DEFAULT_JOB_TITLES, jobTitleKey } from "#app/lib/job-titles";
import { useJobTitles } from "#app/lib/hooks/use-job-titles";

// ---------------------------------------------------------------------------
// JobTitleSelector — the Title property's picker over the job-title
// vocabulary, on the library's creatable-vocabulary grammar: one search-or-add
// field, plain-text rows that pick and close, a row that clears, a live Create
// row for a typed name, and a hover "…" rename/delete editor on the titles the
// viewer may manage (creator or manage.users).
//
// The vocabulary is two populations in ONE scrolling list: the seed titles
// SalesTrak ships, unlabeled up top, then every title this workspace added
// under a "Custom" group heading — the group boundary is what explains the "…"
// affordance, which appears per row only where this viewer may manage. A
// custom that duplicates a seed name collapses into the seed row (the seed
// masks it) and is not manageable there.
//
// Selection writes a FREE STRING — a member's or a contact's title. The
// vocabulary is only a suggestion source, so renames and deletes never touch
// the value anyone already carries, and a value that is not in the vocabulary
// simply checks no row.
// ---------------------------------------------------------------------------

export type JobTitleSelectorProps = Omittable<{
  /** The current title (free string) or null. */
  value: string | null;
  /** Picking a row / creating / clearing commits the new title (null = clear). */
  onValueChange: (title: string | null) => void;
  /** Viewer id — the creator of a custom title may rename/delete it. */
  currentUserId?: string | null;
  /** manage.users — may rename/delete ANY custom title. */
  canManage?: boolean;
  /** Render the value as plain text with no popover affordance. */
  readOnly?: boolean;
  /**
   * Trigger chrome. `inline` (the default) is the bare value + chevron the
   * profile island's identity header wears — the title is the secondary line
   * under the name and must not read as a control. `field` wears the Select
   * trigger chrome so the picker sits in a settings row indistinguishable
   * from the selects beside it.
   */
  trigger?: "inline" | "field";
  /** Rung of the `inline` trigger: "sm" (28px, default), "xs" (24px, the
   *  profile island's property line) or "default" (32px, the drawer property
   *  rows — the editing-input height, so rest and edit share one rung). */
  size?: "xs" | "sm" | "default";
  /** Corners of the `inline` trigger — "square" when it sits as row chrome
   *  among other 24px property values. */
  shape?: React.ComponentProps<typeof Button>["shape"];
  /** The `inline` trigger's caret. Off in a LABELED property row: the row's
   *  "Title" label already says what the control is, the same reason the
   *  Type row's selector drops its chevron. */
  showChevron?: boolean;
  /** Empty-state copy of the `inline` trigger. */
  placeholder?: string;
  /** Ink of the empty `inline` trigger. */
  emptyTextVariant?: React.ComponentProps<typeof Text>["variant"];
  /** Whether a SET value reads muted. On by default — the profile island's
   *  identity header keeps the title quieter than the name above it. A
   *  property row passes false so the value reads like its neighbours. */
  valueMuted?: boolean;
  /**
   * `inline` mounts the list without a popover or trigger of its own, for a
   * host that already owns the popup (a table cell's popover). Every pick
   * reports through `onOpenChange` with `false` so the host can close.
   */
  variant?: CreatableOptionPickerProps["variant"];
  onOpenChange?: CreatableOptionPickerProps["onOpenChange"];
}>;

/** A seed title's row id is the word itself — seeds are not stored rows. */
function seedOption(name: string): CreatableOption {
  return { id: name, label: name };
}

export function JobTitleSelector({
  value,
  onValueChange,
  currentUserId,
  canManage = false,
  readOnly = false,
  trigger = "inline",
  size = "sm",
  shape,
  showChevron = true,
  placeholder = "Add title",
  emptyTextVariant = "muted",
  valueMuted = true,
  variant,
  onOpenChange,
}: JobTitleSelectorProps) {
  const { titles, createTitle, renameTitle, deleteTitle } = useJobTitles();

  // Seeds are the fixed rows; every workspace-added title is a Custom row,
  // editable here only by its creator or a manage.users holder. A custom that
  // duplicates a seed name is masked by the seed row.
  const { fixed, manageable } = useMemo(() => {
    const seedKeys = new Set(DEFAULT_JOB_TITLES.map(jobTitleKey));
    const fixed: CreatableOption[] = DEFAULT_JOB_TITLES.map(seedOption);
    const manageable: CreatableOption[] = [];
    for (const title of titles) {
      if (seedKeys.has(jobTitleKey(title.name))) continue;
      manageable.push({
        editable:
          canManage || (!!currentUserId && title.createdBy === currentUserId),
        id: title.id,
        label: title.name,
      });
    }
    return { fixed, manageable };
  }, [canManage, currentUserId, titles]);

  const canManageAny = manageable.some((option) => option.editable);

  // Which row wears the check: whichever carries the applied words. The value
  // is a free string, so a title outside the vocabulary checks nothing.
  const selectedId = useMemo(() => {
    if (!value?.trim()) return null;
    const key = creatableOptionKey(value);
    return (
      [...fixed, ...manageable].find(
        (option) => creatableOptionKey(option.label) === key,
      )?.id ?? null
    );
  }, [fixed, manageable, value]);

  const hasValue = value !== null && value !== undefined;

  const handleClear = useCallback(() => {
    // Clearing an unset title is already the truth — no no-op update.
    if (hasValue) onValueChange(null);
  }, [hasValue, onValueChange]);

  // Create applies the typed name at once — the title is a free string that
  // never depended on the vocabulary — and adds it to the shared vocabulary
  // for every picker after this one.
  const handleCreate = useCallback(
    (label: string) => {
      onValueChange(label);
      void createTitle(label);
    },
    [createTitle, onValueChange],
  );

  // Vocabulary-only edits: anyone already carrying the old name keeps it.
  const handleRename = useCallback(
    (id: string, name: string) => {
      void renameTitle(id, name);
    },
    [renameTitle],
  );
  const handleDelete = useCallback(
    (id: string) => {
      void deleteTitle(id);
    },
    [deleteTitle],
  );

  if (readOnly) {
    return hasValue ? (
      <Text size="sm" truncate={1}>
        {value}
      </Text>
    ) : (
      <Text size="sm" variant="muted" truncate={1}>
        No title
      </Text>
    );
  }

  return (
    <CreatableOptionPicker
      options={fixed}
      manageableOptions={manageable}
      selectedId={selectedId}
      onPick={(option) => onValueChange(option.label)}
      onClear={handleClear}
      // Any member may add to the shared vocabulary.
      onCreate={handleCreate}
      onRename={canManageAny ? handleRename : undefined}
      onDelete={canManageAny ? handleDelete : undefined}
      manageableLabel="Custom"
      noun="Title"
      searchPlaceholder="Search or add a title..."
      onOpenChange={onOpenChange}
      variant={variant}
    >
      {trigger === "field" ? (
        // Settings row: the exact SelectTrigger anatomy (flex-1 value +
        // chevrons) so the Title row is indistinguishable from the selects it
        // sits among. Unset reads as "—" — the row label already says Title,
        // so a "Select a title" placeholder would only restate it.
        // `full` so a host that gives the control column a definite width
        // (a settings group aligning the select with the inputs above it)
        // gets a trigger that spans it.
        <FieldTrigger aria-label="Title" value={hasValue ? value : undefined} />
      ) : (
        <Button
          variant="ghost"
          size={size}
          shape={shape}
          justify="start"
          // The value is content, not a label: normal weight, left-aligned,
          // and the button yields width so the title can truncate.
          className="min-w-0 shrink font-normal"
        >
          <Box shrink minWidth={0}>
            {/* A button centers its text; a clamped title would center its
                one line inside the box and read as indented. */}
            {hasValue ? (
              <Text
                align="left"
                size="sm"
                truncate={1}
                {...(valueMuted ? { variant: "muted" as const } : {})}
              >
                {value}
              </Text>
            ) : (
              <Text align="left" size="sm" variant={emptyTextVariant} truncate={1}>
                {placeholder}
              </Text>
            )}
          </Box>
          {/* The 24px property line carries 16px glyphs, matching the row's
              leading icon; the 28px rung keeps the 14px caret. */}
          {showChevron ? (
            <Icon
              name="chevron-down"
              size={size === "xs" ? "sm" : "xs"}
              color="muted"
            />
          ) : null}
        </Button>
      )}
    </CreatableOptionPicker>
  );
}
